import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Button, Chip, Snackbar, Alert } from "@mui/material";
import { AppGridColDef, EditableDataGridStyle } from "../../shared/EditableDataGridStyle";
import { PublicAction, PublicLinkActionType } from "../../interfaces/DTO/PublicAction";
import { publicActionService } from "../../services/PublicActionService";
import { preadviceTasksService } from "../../services/PreadviceTasksService";
import { buildPublicTokenUrl, formatPublicLinkActionType } from "../../utils/featuresFormatter";
import { useContactNameColumn } from "../../hooks/useContactNameColumn";
import { enumColumn, isoDateTimeColumn } from "../../shared/UIParts/columnsFormatter";
import { formatIsoDateWithTime } from "../../utils/namesFormater";

interface ConsentActionsTableProps {
  onRequestPreAdvice?: (contactId: number) => void;
}

// קומפוננטה נפרדת לכפתור כדי להימנע משימוש ב-hooks בתוך renderCell
const RequestPreAdviceButton: React.FC<{
  contactId: number | undefined;
  isSigned: boolean;
  onRequestPreAdvice?: (contactId: number) => void;
}> = ({ contactId, isSigned, onRequestPreAdvice }) => {
  const [requesting, setRequesting] = useState(false);

  const handleClick = async () => {
    if (!contactId) return;

    setRequesting(true);
    try {
     
      const result =await preadviceTasksService.requestPreAdviceForContact(contactId);

      if (onRequestPreAdvice) {
        onRequestPreAdvice(contactId);
      }
       if (result.success) {
            alert("בקשת טרום ייעוץ נשלחה בהצלחה!");
          } else {
           alert(`שגיאה בשליחת בקשה: ${result?.message}`);
          }
      // alert("בקשת טרום ייעוץ נשלחה בהצלחה!");
    } catch (error: any) {
      alert(`שגיאה בשליחת בקשה: ${error?.response?.data?.detail || error.message}`);
    } finally {
      setRequesting(false);
    }
  };

  return (
    <Button
      variant="contained"
      size="small"
      disabled={!isSigned || requesting}
      onClick={handleClick}
    >
      {requesting ? "שולח..." : "הזמן טרום ייעוץ"}
    </Button>
  );
};

const ConsentActionsTable: React.FC<ConsentActionsTableProps> = ({ onRequestPreAdvice }) => {
    const [data, setData] = useState<PublicAction[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
      open: false,
      message: "",
      severity: "success",
    });

    useEffect(() => {
      const loadData = async () => {
        try {
          const allActions = await publicActionService.getAll();
          // סינון - רק פעולות מסוג CONSENT
          const consentActions = allActions.filter(
            (action) =>
              action.action_type === PublicLinkActionType.VIEW_P_CONSENT ||
              action.action_type === PublicLinkActionType.SIGN_CONSENT
          );
          setData(consentActions);
        } catch (e) {
          console.error("שגיאה בטעינת פעולות הסכמה", e);
        } finally {
          setLoading(false);
        }
      };

      loadData();
    }, []);

    const contactNameColumn = useContactNameColumn();

    const columns: AppGridColDef<PublicAction>[] = useMemo(() => [
      contactNameColumn,
      enumColumn("action_type","סוג הסכמה",Object.values(PublicLinkActionType),formatPublicLinkActionType, 150),

      isoDateTimeColumn("created_at", "נוצר בתאריך", 180),

      {
        field: "filled_at",
        headerName: "נחתם בתאריך",
        width: 180,
        type: "dateTime",
        valueFormatter: (value) => value ? new Date(value).toLocaleString('he-IL') : "-",
        renderCell: (params) => {
          if (params.value) {
            return (
              <Chip
               // label={new Date(params.value).toLocaleString('he-IL')}
                label={formatIsoDateWithTime(params.value)}
                color="success"
                size="small"
              />
            );
          }
          return <Chip label="לא נחתם" color="warning" size="small" />;
        }
      },
      isoDateTimeColumn("expires_at", "תוקף", 180),

      {
        field: "is_used",
        headerName: "האם נוצל",
        width: 130,
        type: "boolean",
      },
      {
        field: "secret",
        headerName: "קישור",
        width: 150,
        renderCell: (params) => {
          const url = buildPublicTokenUrl(params.row.secret);
          return (
            <button
              style={{ cursor: "pointer", background: "none", border: "none", color: "blue" }}
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(url).then(() => {
                  setSnackbar({ open: true, message: "הקישור הועתק בהצלחה", severity: "success" });
                }).catch((err) => {
                  console.error('Failed to copy:', err);
                  setSnackbar({ open: true, message: "שגיאה בהעתקת הקישור", severity: "error" });
                });
              }}
            >
              העתק קישור
            </button>
          );
        },
      },
      {
        field: "actions",
        headerName: "פעולות",
        width: 200,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const isSigned = !!params.row.filled_at;
          return (
            <RequestPreAdviceButton
              contactId={params.row.contact_id}
              isSigned={isSigned}
              onRequestPreAdvice={onRequestPreAdvice}
            />
          );
        },
      },
    ], [contactNameColumn, onRequestPreAdvice]);

    const handlePartialUpdate = async (id: string | number, fields: Partial<PublicAction>) => {
      if (typeof id === "number") {
        await publicActionService.partialUpdate(id, fields);
      }
    };

    const handleDeleteRows = async (ids: (string | number)[]) => {
      for (const id of ids) {
        if (typeof id === "number") {
          await publicActionService.delete(id);
        }
      }
    };

    const handleCreateNew = async (fields: Partial<PublicAction>) => {
      const newToken = await publicActionService.create(fields);
      setData((prev) => [...prev, newToken]);
    };

    return (
      <Box sx={{ width: "100%", direction: "rtl" }}>
        <Typography variant="h6" gutterBottom>
          טבלת הסכמות
        </Typography>

        <EditableDataGridStyle<PublicAction>
          columns={columns}
          initialRows={data}
          onPartialUpdate={handlePartialUpdate}
          onDeleteRows={handleDeleteRows}
          onCreateNew={handleCreateNew}
          emptyRow={PublicAction.defaultEmpty()}
          loading={loading}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  };

  export default ConsentActionsTable;
